import { CheckCircle2, Circle, Loader } from 'lucide-react';
import { usePrototype1, ScreenId } from '../Prototype1Context';
import { SECTIONS, SectionStatus, SectionMeta } from '../prototype1Data';

const ACTION_BY_STATUS: Record<SectionStatus, string> = {
  complete: 'Edit Section',
  'in-progress': 'Continue Editing',
  'not-started': 'Start Section',
};

const targetScreenForSection = (section: SectionMeta): ScreenId => {
  if (section.id === 'college-eligibility' && section.status === 'not-started') {
    return '02-college-eligibility-not-started';
  }
  if (section.status === 'in-progress') return '06-section-editor-ai-draft';
  return '06-section-editor-ai-draft';
};

const StatusIcon = ({ status }: { status: SectionStatus }) => {
  if (status === 'complete') return <CheckCircle2 className="w-4 h-4 text-[#16a34a]" />;
  if (status === 'in-progress') return <Loader className="w-4 h-4 text-[#f59e0b] animate-spin" />;
  return <Circle className="w-4 h-4 text-[#d4d4d4]" />;
};

const StatusLabel = ({ status }: { status: SectionStatus }) => {
  if (status === 'complete') return <span className="text-xs font-medium text-[#16a34a] font-['Inter']">Complete</span>;
  if (status === 'in-progress')
    return <span className="text-xs font-medium text-[#f59e0b] font-['Inter']">In Progress</span>;
  return <span className="text-xs text-[#737373] font-['Inter']">Not Started</span>;
};

const Screen01Dashboard = () => {
  const { goToScreen, setActiveSectionId, sectionStatuses, allSectionsComplete } = usePrototype1();
  const completedCount = SECTIONS.filter((s) => (sectionStatuses[s.id] ?? s.status) === 'complete').length;
  const percent = Math.round((completedCount / SECTIONS.length) * 100);

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <span className="text-sm text-[#0a0a0a] font-medium">Dashboard</span>
        </div>

        <div className="flex flex-col gap-6 p-6 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4 p-6 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-lg font-semibold text-[#0a0a0a]">Issaquah School District — Class of 2025</span>
                <span className="text-sm text-[#737373]">
                  EOAA Report · {completedCount} of {SECTIONS.length} sections complete
                </span>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold text-white rounded-full shrink-0 ${
                  allSectionsComplete ? 'bg-[#16a34a]' : 'bg-[#f59e0b]'
                }`}
              >
                {allSectionsComplete ? 'Complete' : 'In Progress'}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="w-full h-2 rounded-full bg-[#e5e5e5] overflow-hidden">
                <div className="h-full rounded-full bg-[#171717]" style={{ width: `${percent}%` }} />
              </div>
              <span className="text-xs text-[#737373]">{percent}% complete</span>
            </div>
          </div>

          <span className="text-base font-semibold text-[#0a0a0a]">Report Sections</span>
          <div className="flex flex-col gap-3">
            {SECTIONS.map((s) => {
              const status = sectionStatuses[s.id] ?? s.status;
              return (
                <div
                  key={s.id}
                  className="flex flex-col gap-3 p-5 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={status} />
                      <span className="text-sm font-semibold text-[#0a0a0a]">{s.title}</span>
                    </div>
                    <StatusLabel status={status} />
                  </div>
                  <p className="text-[13px] text-[#737373] leading-[1.5]">{s.description}</p>
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSectionId(s.id);
                        goToScreen(targetScreenForSection({ ...s, status }));
                      }}
                      className={`px-3 py-1.5 text-[13px] font-medium rounded-md border transition-colors ${
                        status === 'in-progress'
                          ? 'bg-[#171717] text-white border-[#171717] hover:bg-[#2a2a2a]'
                          : 'bg-white text-[#0a0a0a] border-[#e5e5e5] hover:bg-gray-50'
                      }`}
                    >
                      {ACTION_BY_STATUS[status]}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen01Dashboard;
